import React, { useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import useTimer from "./useTimer";
import DoctorImg from "../../../assets/images/doctor1.png";
import ExamImg from "../../../assets/images/examination1.png";
import MicImg from "../../../assets/images/mic.png";
import RecordImg from "../../../assets/images/record.svg";

import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import PlayCircleFilledOutlined from "@material-ui/icons/PlayCircleFilledOutlined";
import WaveSurfer from "wavesurfer.js";
import AudioRecorder from "./AudioRecoreder";
import ApiService from "../../../ApiService";
import axios from "axios";
import { TranscribeConvertService } from "./TranscribeConvertService";
import ReactQuill from "react-quill";

const formatTime = timer => {
  const getSeconds = `0${timer % 60}`.slice(-2);
  const minutes = `${Math.floor(timer / 60)}`;
  const getMinutes = `0${minutes % 60}`.slice(-2);
  const getHours = `0${Math.floor(timer / 3600)}`.slice(-2);

  return `${getHours} : ${getMinutes} : ${getSeconds}`;
};
export default function RecordSection({
  setModalStage,
  patientName,
  clinicianName,
  fileId,
  transcribeAudioUrl,
  transcribeOutputUrl,
  transcribeSummaryUrl,
  transcribeStatus,
  transcribeData,
  setTranscribeData,
  errorSuccessHandler,
  isTranscribeConsumed
}) {
  const { timer, isActive, isPaused, handleStart, handlePause, handleResume, handleReset } = useTimer(0);
  const wavesurferRef = useRef(null);

  const [audio, setAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSubmitForTranscribe, setIsSubmitForTranscribe] = useState(false);
  const [transcribeMode, setTranscribeMode] = useState("conversation");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    if (transcribeStatus === "AUDIO_UPLOADED" && transcribeAudioUrl) {
      setAudio(transcribeAudioUrl);
      setIsSubmitForTranscribe(false);
      setLoading(false);
    }
    if (transcribeStatus === "AWAITING_TRANSCRIBED_OUTPUT" && transcribeAudioUrl) {
      setAudio(transcribeAudioUrl);
      setIsSubmitForTranscribe(false);
      setLoading(false);
    }

    if (transcribeStatus === "COMPLETED" && transcribeOutputUrl && transcribeSummaryUrl) {
      setAudio(transcribeAudioUrl);
      getTranscribeOutput(transcribeOutputUrl, transcribeSummaryUrl);
      setIsSubmitForTranscribe(true);
      setLoading(false);
    }
  }, [transcribeAudioUrl, transcribeOutputUrl, transcribeStatus, transcribeSummaryUrl]);

  const [jsonDataResponse, setJsonDataResponse] = useState(null);
  const [summaryDataResponse, setSummaryDataResponse] = useState(null);

  const getTranscribeOutput = async (transcribeOutputUrl, transcribeSummaryUrl) => {
    try {
      const jsonDataResponse = await axios.get(transcribeOutputUrl);
      const summaryDataResponse = await axios.get(transcribeSummaryUrl);
      setJsonDataResponse(jsonDataResponse?.data);
      setSummaryDataResponse(summaryDataResponse?.data);
      if (transcribeMode === "conversation") {
        setTranscribeData(TranscribeConvertService.conversation(jsonDataResponse?.data));
      } else {
        setTranscribeData(TranscribeConvertService.summary(summaryDataResponse?.data?.ClinicalDocumentation?.Sections));
      }
    } catch (error) {
      console.log(error);
    }
  };
  const onChangeTranscribeMode = e => {
    setTranscribeMode(e.target.value);
    if (e.target.value === "conversation") {
      setTranscribeData(TranscribeConvertService.conversation(jsonDataResponse));
    } else {
      setTranscribeData(TranscribeConvertService.summary(summaryDataResponse?.ClinicalDocumentation?.Sections));
    }
  };

  useEffect(() => {
    // Initialize Wavesurfer when the component mounts
    if (audio) {
      wavesurferRef.current = WaveSurfer.create({
        container: "#waveform-container",
        waveColor: "#b2beb5",
        progressColor: "#EE8155",
        height: 100,
        barWidth: 2, // Reduced for performance
        cursorWidth: 1, // Reduced for performance
        pixelRatio: 1, // Can reduce for performance
        responsive: true,
        backend: "MediaElement" // 'MediaElement' can sometimes offer better performance
      });

      // Load audio when the component updates with a new audio URL
      wavesurferRef.current.load(audio);

      return () => {
        // Clean up Wavesurfer instance when the component unmounts
        wavesurferRef.current.destroy();
      };
    }
  }, [audio, isSubmitForTranscribe]);

  const submitForTranscribe = async () => {
    if (transcribeStatus === "AWAITING_TRANSCRIBED_OUTPUT") {
      alert("Transcription is already in progress");
      return;
    }
    try {
      const res = await ApiService.submitForTranscribe(fileId);
      if (res?.data) {
        setIsSubmitForTranscribe(true);
        // wavesurferRef.current.finish();
        setIsPlaying(false);
      }
    } catch (error) {
      console.log(error);
      alert(error.message);
    }
  };

  const discardTranscribe = async () => {
    try {
      const res = ApiService.updateTranscribeStatus(fileId, {
        status: "DISCARDED"
      });
      if (res) {
        setAudio(null);
        setIsPlaying(false);
        setIsSubmitForTranscribe(false);
        errorSuccessHandler(false, true, "Transcription discarded successfully");
      }
    } catch (error) {
      errorSuccessHandler(true, false, error.message);
    }
  };

  return (
    <section className="recorder-section">
      {isSubmitForTranscribe ? (
        <>
          <div className="mb-2 mt-3" style={{ display: "flex", justifyContent: "space-between", gap: "50px" }}>
            <div style={{ flex: 1 }} id="waveform-container" />
            {isPlaying ? (
              <Button
                style={{ borderRadius: "16px", alignSelf: "center" }}
                className="btn-light"
                onClick={() => {
                  setIsPlaying(false);
                  wavesurferRef.current.pause();
                }}
              >
                <PauseCircleOutlineIcon style={{ fill: "#000" }} />
              </Button>
            ) : (
              <Button
                style={{ borderRadius: "16px", alignSelf: "center" }}
                className="btn-light"
                onClick={() => {
                  setIsPlaying(true);
                  wavesurferRef.current.play();
                }}
              >
                <PlayCircleFilledOutlined style={{ fill: "#000" }} />
              </Button>
            )}
          </div>
          {transcribeStatus === "COMPLETED" ? (
            <>
              <div className="mb-2" style={{ display: "flex", justifyContent: "space-between" }}>
                <h3>Transcription</h3>
                <div>
                  <Form.Control as="select" className="mr-2" onChange={onChangeTranscribeMode}>
                    <option value="conversation">Conversation</option>
                    <option value="summary">Summary</option>
                  </Form.Control>
                </div>
              </div>

              {/* <textarea
                rows={20}
                style={{ width: "100%", background: "#e9ecef" }}
                value={transcribeData}
                onChange={e => {
                  setTranscribeData(e.target.value);
                }}
              /> */}
              <div>
                <style>
                  {`
                          .ql-editor {
                             min-height: 300px; 
                             max-height:300px;
                          }
                      `}
                </style>

                <ReactQuill
                  theme="snow"
                  value={transcribeData}
                  onChange={value => {
                    setTranscribeData(value);
                  }}
                />
              </div>
            </>
          ) : (
            <div>
              <h3> You will receive an email when the transcription output is ready.</h3>
            </div>
          )}

          {!isTranscribeConsumed && transcribeStatus === "COMPLETED" && transcribeMode === "summary" && (
            <div className="mt-2" style={{ textAlign: "end" }}>
              <Button
                onClick={() => {
                  discardTranscribe();
                }}
                className="discard-btn mr-2"
              >
                Discard
              </Button>
              <Button onClick={() => setModalStage("save_as")} className="record-btn">
                Save as
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          {isActive && (
            <div className="record-img">
              <img src={RecordImg} alt="" />
            </div>
          )}

          {!audio && <h1 className="pb-2 text-center">{formatTime(timer)}</h1>}

          <div className="grid-2">
            <div className="mt-4 text-center">
              <img src={DoctorImg} alt="" />
              <Form.Control className="mt-1" type="product" value={clinicianName} />
            </div>

            <div className="text-center">
              {audio ? (
                <div className="my-5">
                  <div id="waveform-container" />
                  <div className="mt-1" style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>{formatTime(0)}</div>
                    <div>{formatTime(Math.floor(wavesurferRef?.current?.getDuration()))}</div>
                  </div>
                  {isPlaying ? (
                    <Button
                      style={{ borderRadius: "16px" }}
                      className="btn-light"
                      onClick={() => {
                        setIsPlaying(false);
                        wavesurferRef.current.pause();
                      }}
                    >
                      <PauseCircleOutlineIcon style={{ fill: "#000" }} />
                    </Button>
                  ) : (
                    <Button
                      style={{ borderRadius: "16px" }}
                      className="btn-light"
                      onClick={() => {
                        setIsPlaying(true);
                        wavesurferRef.current.play();
                      }}
                    >
                      <PlayCircleFilledOutlined style={{ fill: "#000" }} />
                    </Button>
                  )}

                  {transcribeStatus === "AWAITING_TRANSCRIBED_OUTPUT" ? (
                    <div
                      style={{
                        marginTop: "20px",
                        paddingTop: "50px"
                      }}
                    >
                      <h3> You will receive an email notification when your transcription is complete.</h3>
                    </div>
                  ) : (
                    <div className="mt-5 pt-5">
                      <Button
                        onClick={() => {
                          discardTranscribe();
                        }}
                        className="discard-btn mr-2"
                      >
                        Discard
                      </Button>
                      <Button onClick={submitForTranscribe} className="record-btn">
                        Submit for Transcribe
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <img src={MicImg} alt="" />
                  <AudioRecorder
                    setAudio={setAudio}
                    handlePause={handlePause}
                    handleReset={handleReset}
                    handleResume={handleResume}
                    handleStart={handleStart}
                    fileId={fileId}
                  />
                </>
              )}
            </div>
            <div className="mt-4 text-center">
              <img src={ExamImg} alt="" />
              <Form.Control className="mt-1" type="product" value={patientName} />
            </div>
          </div>
        </>
      )}
    </section>
  );
}
